import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect, useRef, useCallback } from 'react';
import ModalComponent from '../../commons/modalComponent';
import { useModal } from '../../../../application/utils/useModal';
// Styles
import '../../../assets/saas/molecules/_lightbox-gallery.scss';
import IconPlayVideo from '../../../assets/iconPlayVideo';
import { ImagesPreviewVideo } from './imagesPreviewVideos/ImagesPreviewVideo';
import i18next from 'i18next';
import ImageRotator from '../../commons/ImageRotator';
import { transformRotatorUrls } from '../../../hooks/ImageHook';
import Icon360View from '../../../assets/icon360View';
import { useResponsiveScreen } from '../../../hooks/useResponsiveScreen';
const NavPill360 = ({ frameIndex, frameCount, onFrameChange, pillWidth = 72, pillHeight: pillHeightOverride, dragStepPx, isVisible = true, extraClassName, followCursor = false, }) => {
    const navRef = useRef(null);
    const isDragging = useRef(false);
    const lastX = useRef(0);
    const dragPos = useRef(0);
    const [dragging, setDragging] = useState(false);
    const pillStep = dragStepPx !== null && dragStepPx !== void 0 ? dragStepPx : (pillWidth / 3);
    const baseFontSize = Math.max(12, Math.min(16, Math.round(pillWidth * 0.15)));
    const pillHeight = pillHeightOverride !== null && pillHeightOverride !== void 0 ? pillHeightOverride : Math.max(16, Math.round(pillWidth * 0.22));
    const getPillOffset = useCallback((fi) => {
        if (frameCount <= 1)
            return 0;
        const half = ((frameCount - 1) * pillStep) / 2;
        return half - fi * pillStep;
    }, [frameCount, pillStep]);
    const applyTransform = useCallback((px, animate) => {
        if (!navRef.current)
            return;
        navRef.current.style.transition = animate
            ? 'transform 0.35s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
            : 'none';
        navRef.current.style.transform = `translateX(calc(-50% + ${px}px))`;
    }, []);
    useEffect(() => {
        if (!isDragging.current && !followCursor) {
            applyTransform(getPillOffset(frameIndex), true);
        }
    }, [frameIndex, applyTransform, getPillOffset, followCursor]);
    const onPointerDown = (e) => {
        e.preventDefault();
        dragPos.current = getPillOffset(frameIndex);
        lastX.current = e.clientX;
        isDragging.current = true;
        setDragging(true);
        applyTransform(dragPos.current, false);
        e.currentTarget.setPointerCapture(e.pointerId);
    };
    const onPointerMove = (e) => {
        if (!isDragging.current)
            return;
        const delta = e.clientX - lastX.current;
        lastX.current = e.clientX;
        const half = ((frameCount - 1) * pillStep) / 2;
        dragPos.current = Math.max(-half, Math.min(half, dragPos.current + delta));
        applyTransform(dragPos.current, false);
        const frameFloat = (half - dragPos.current) / pillStep;
        onFrameChange(Math.max(0, Math.min(frameCount - 1, Math.round(frameFloat))));
    };
    const onPointerUp = () => {
        if (!isDragging.current)
            return;
        isDragging.current = false;
        setDragging(false);
        applyTransform(getPillOffset(frameIndex), true);
    };
    const handlePrev = () => onFrameChange(Math.max(0, frameIndex - 1));
    const handleNext = () => onFrameChange(Math.min(frameCount - 1, frameIndex + 1));
    const shouldShow = isVisible || dragging;
    return (_jsxs("div", { ref: navRef, className: `lightbox-gallery__360-nav${extraClassName ? ` ${extraClassName}` : ''}`, onPointerDown: onPointerDown, onPointerMove: onPointerMove, onPointerUp: onPointerUp, onPointerLeave: onPointerUp, style: {
            cursor: followCursor ? 'none' : (dragging ? 'grabbing' : 'grab'),
            ...(!followCursor && { transform: `translateX(calc(-50% + ${getPillOffset(frameIndex)}px))` }),
            width: `${pillWidth}px`,
            height: `${pillHeight}px`,
            opacity: shouldShow ? 1 : 0,
            transition: 'opacity 300ms ease-in-out',
            pointerEvents: followCursor ? 'none' : (shouldShow ? 'auto' : 'none'),
        }, children: [_jsx("button", { className: "lightbox-gallery__360-nav-btn", onClick: handlePrev, "aria-label": "Previous frame", style: { fontSize: `${baseFontSize + 6}px` }, children: "\u2039" }), _jsx("span", { className: "lightbox-gallery__360-nav-label", children: "360\u00B0" }), _jsx("button", { className: "lightbox-gallery__360-nav-btn", onClick: handleNext, "aria-label": "Next frame", style: { fontSize: `${baseFontSize + 6}px` }, children: "\u203A" })] }));
};
const LightboxGalleryImage = ({ imagesUrl, OGMainImage, startOpen = false, onClose, openOnRotator = false }) => {
    const { isShown: isModalOpen, toggle } = useModal();
    const { isMobile } = useResponsiveScreen();
    const modalPillDragStep = 64;
    // Modal state
    const [currentImageIndex, setCurrentImageIndex] = useState(OGMainImage);
    const [type, setType] = useState("image");
    const [videoURL, setVideoURL] = useState("");
    const [rotatorImages, setRotatorImages] = useState([]);
    const [rotatorFrameIndex, setRotatorFrameIndex] = useState(0);
    const [loading, setLoading] = useState(true);
    // Inline view state (details page, not modal)
    const [inlineType, setInlineType] = useState("image");
    const [inlineImage, setInlineImage] = useState(OGMainImage);
    const [inlineMediaType, setInlineMediaType] = useState("image");
    const [inlineRotatorImages, setInlineRotatorImages] = useState([]);
    const [inlineFrameIndex, setInlineFrameIndex] = useState(0);
    const [isInlineRotatorHovered, setIsInlineRotatorHovered] = useState(false);
    const [isModalRotatorHovered, setIsModalRotatorHovered] = useState(false);
    const [isModalRotatorDragging, setIsModalRotatorDragging] = useState(false);
    const modalRotatorSectionRef = useRef(null);
    const modalPillWrapperRef = useRef(null);
    const inlinePillWrapperRef = useRef(null);
    // Modal drag state (refs to avoid re-renders during drag)
    const modalDragActive = useRef(false);
    const modalDragLastX = useRef(0);
    const modalDragAccum = useRef(0);
    const [inlineRotatorFailed, setInlineRotatorFailed] = useState(false);
    const [modalRotatorFailed, setModalRotatorFailed] = useState(false);
    const [isInlineRotatorDragging, setIsInlineRotatorDragging] = useState(false);
    const inlineRotatorSectionRef = useRef(null);
    // Inline drag state (refs to avoid re-renders during drag)
    const inlineDragActive = useRef(false);
    const inlineDragLastX = useRef(0);
    const inlineDragAccum = useRef(0);
    const inlineDragDidMove = useRef(false);
    const inlineRotatorRef = useRef(null);
    const [inlinePillWidth, setInlinePillWidth] = useState(72);
    const parseRotatorImages = useCallback((value) => {
        if (!value)
            return [];
        if (Array.isArray(value)) {
            return value.map((img) => (img || "").trim()).filter(Boolean);
        }
        const raw = value.trim();
        if (!raw)
            return [];
        if (raw.startsWith("[") && raw.endsWith("]")) {
            try {
                const parsed = JSON.parse(raw);
                if (Array.isArray(parsed)) {
                    return parsed.map((img) => String(img || "").trim()).filter(Boolean);
                }
            }
            catch (_a) {
                // Ignore and fallback to CSV parsing.
            }
        }
        const splitByHttp = raw
            .split(/,(?=\s*https?:\/\/)/i)
            .map((img) => img.trim().replace(/^"+|"+$/g, ""))
            .filter(Boolean);
        if (splitByHttp.length > 1)
            return splitByHttp;
        if (/^https?:\/\//i.test(raw))
            return [raw.replace(/^"+|"+$/g, "")];
        return [];
    }, []);
    const expandRotatorFramesFromSeed = useCallback((seedUrl) => {
        const clean = String(seedUrl || "").trim();
        const match = clean.match(/^(.*_)(\d{3})(\.[^/?#]+(?:\?[^#]*)?)$/i);
        if (!match)
            return clean ? [clean] : [];
        const [, prefix, , suffix] = match;
        return [45, 90, 135, 180, 225, 270, 315, 360].map((angle) => `${prefix}${String(angle).padStart(3, "0")}${suffix}`);
    }, []);
    const getRotatorFrames = useCallback((media) => {
        if (!media || typeof media === "string")
            return [];
        const declaredAsRotator = String(media.type || "").toLowerCase().includes("rotator");
        const framesFromImages = parseRotatorImages(media.images);
        const framesFromUrl = parseRotatorImages(media.url);
        const frames = framesFromImages.length ? framesFromImages : (declaredAsRotator || framesFromUrl.length > 1 ? framesFromUrl : []);
        if (declaredAsRotator && frames.length <= 1 && media.url) {
            return expandRotatorFramesFromSeed(media.url);
        }
        if (frames.length)
            return frames;
        return [];
    }, [parseRotatorImages, expandRotatorFramesFromSeed]);
    const isRotatorMedia = useCallback((media) => {
        if (typeof media === "string")
            return false;
        return String(media.type || "").toLowerCase().includes("rotator") || getRotatorFrames(media).length > 0;
    }, [getRotatorFrames]);
    // Rotator always goes FIRST, no duplicates — but hidden on mobile
    const rotatorItem = imagesUrl.find((media) => isRotatorMedia(media));
    // 360 disabled for now. Keep this selector to re-enable quickly.
    // const first360Item = imagesUrl.find(
    //   (media) => typeof media !== "string" && String((media as MediaUrl).type || "").toLowerCase() === "360"
    // ) as MediaUrl | undefined;
    const nonRotatorItems = imagesUrl.filter((media) => !isRotatorMedia(media) &&
        // 360 disabled for now. Remove this condition to show 360 media again.
        !(typeof media !== "string" && String(media.type || "").toLowerCase() === "360"));
    const mediaList = isMobile ? nonRotatorItems : (rotatorItem ? [rotatorItem, ...nonRotatorItems] : nonRotatorItems);
    // Initialize inline rotator from rotatorItem (desktop only)
    useEffect(() => {
        setInlineRotatorFailed(false);
        const frames = getRotatorFrames(rotatorItem);
        if (!isMobile && frames.length) {
            setInlineRotatorImages(transformRotatorUrls(frames));
            setInlineType("rotator");
            setInlineMediaType("rotator");
        }
        else {
            setInlineType("image");
            // 360 disabled for now. Re-enable fallback block below if needed.
            // if (first360Item?.url) {
            //   setInlineImage(first360Item.url);
            //   setInlineMediaType("360");
            // } else {
            setInlineImage(OGMainImage);
            setInlineMediaType("image");
            // }
        }
    }, [isMobile, rotatorItem, OGMainImage, getRotatorFrames]);
    const getPoster = (input) => {
        const regex = /\/manifest\/[^/]+\.m3u8/;
        return input.replace(regex, '/thumbnails/thumbnail.jpg?width=1920&height=1080');
    };
    useEffect(() => {
        if (startOpen && !isModalOpen) {
            if (openOnRotator) {
                const rotator = mediaList.find((media) => isRotatorMedia(media));
                const frames = getRotatorFrames(rotator);
                if (rotator) {
                    setType("rotator");
                    const cdnFrames = transformRotatorUrls(frames);
                    setRotatorImages(cdnFrames);
                    setCurrentImageIndex(cdnFrames[0] || rotator.url || OGMainImage);
                    setRotatorFrameIndex(0);
                    // 360 disabled for now. Re-enable fallback block below if needed.
                    // } else {
                    //   const first360Media = mediaList.find(
                    //     (media) => typeof media !== "string" && String((media as MediaUrl).type || "").toLowerCase() === "360"
                    //   ) as MediaUrl | undefined;
                    //   if (first360Media?.url) {
                    //     setType("360");
                    //     setCurrentImageIndex(first360Media.url);
                    //   }
                }
            }
            toggleModal();
        }
    }, [startOpen, openOnRotator, mediaList, isRotatorMedia, getRotatorFrames, OGMainImage, isModalOpen]);
    useEffect(() => {
        if (isModalOpen) {
            setLoading(false);
        }
    }, [isModalOpen, currentImageIndex]);
    const handleMediaSelection = (url, mediaType, urlVideo, images) => {
        if (currentImageIndex == url && type == (mediaType || "image")) {
            setLoading(false);
        }
        else {
            setLoading(true);
            setCurrentImageIndex(url);
            setType(mediaType || "image");
            setRotatorImages(mediaType === "rotator" ? transformRotatorUrls(images || []) : []);
            setRotatorFrameIndex(0);
            if (mediaType !== "rotator") {
                setModalRotatorFailed(false);
            }
            setVideoURL(url.includes('.m3u8') ? url :
                urlVideo || "");
        }
    };
    const toggleModal = () => {
        if (isModalOpen && onClose) {
            onClose();
        }
        toggle();
    };
    const openModal = (url, mediaType, urlVideo, images) => {
        handleMediaSelection(url, mediaType, urlVideo, images);
        toggleModal();
    };
    useEffect(() => {
        const node = inlineRotatorRef.current;
        if (!node || typeof ResizeObserver === "undefined")
            return;
        const updatePillWidth = () => {
            const rect = node.getBoundingClientRect();
            const side = Math.min(rect.width || 0, rect.height || rect.width || 0);
            if (!side)
                return;
            const nextWidth = Math.max(72, Math.min(104, Math.round(side * 0.26)));
            setInlinePillWidth((prev) => (prev === nextWidth ? prev : nextWidth));
        };
        const observer = new ResizeObserver(() => updatePillWidth());
        observer.observe(node);
        updatePillWidth();
        return () => observer.disconnect();
    }, [inlineType, inlineRotatorImages.length]);
    // Inline thumbnail click: switch inline view (rotator vs image), click on image opens modal
    const handleInlineThumbnailClick = (media, mediaType, images) => {
        if (mediaType === "rotator" && (images === null || images === void 0 ? void 0 : images.length)) {
            setInlineType("rotator");
            setInlineMediaType("rotator");
            setInlineRotatorImages(transformRotatorUrls(images));
            setInlineFrameIndex(0);
        }
        else if (typeof media === "string") {
            setInlineType("image");
            setInlineMediaType("image");
            setInlineImage(media);
        }
        else {
            setInlineType("image");
            setInlineMediaType(media.type || "image");
            setInlineImage(media.url || OGMainImage);
        }
    };
    return (_jsxs("div", { className: 'lightbox-gallery', children: [!startOpen && (_jsxs("div", { className: "lightbox-gallery__wrapper", children: [_jsx("div", { className: "lightbox-gallery__mainimage", children: inlineType === "rotator" && inlineRotatorImages.length > 0 && !inlineRotatorFailed ? (_jsxs("section", { ref: (el) => {
                                inlineRotatorRef.current = el;
                                inlineRotatorSectionRef.current = el;
                            }, className: "lightbox-gallery__image--rotator", style: { position: 'relative', width: '100%', height: '100%', touchAction: 'none', cursor: inlineDragActive.current ? 'grabbing' : (isInlineRotatorHovered ? 'none' : 'pointer') }, onMouseEnter: () => setIsInlineRotatorHovered(true), onMouseLeave: () => setIsInlineRotatorHovered(false), onPointerDown: (e) => {
                                e.preventDefault();
                                inlineDragActive.current = true;
                                inlineDragDidMove.current = false;
                                inlineDragLastX.current = e.clientX;
                                inlineDragAccum.current = 0;
                                setIsInlineRotatorDragging(true);
                                e.currentTarget.setPointerCapture(e.pointerId);
                            }, onPointerUp: () => {
                                inlineDragActive.current = false;
                                setIsInlineRotatorDragging(false);
                            }, onPointerLeave: () => {
                                inlineDragActive.current = false;
                                setIsInlineRotatorDragging(false);
                                setIsInlineRotatorHovered(false);
                            }, onPointerMove: (e) => {
                                var _a;
                                // Update pill position (direct DOM, no re-render)
                                const rect = (_a = inlineRotatorSectionRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
                                if (rect && inlinePillWrapperRef.current) {
                                    const pctX = Math.max(5, Math.min(95, ((e.clientX - rect.left) / rect.width) * 100));
                                    const pctY = Math.max(5, Math.min(95, ((e.clientY - rect.top) / rect.height) * 100));
                                    inlinePillWrapperRef.current.style.top = `${pctY}%`;
                                    inlinePillWrapperRef.current.style.left = `${pctX}%`;
                                }
                                // Handle drag rotation
                                if (!inlineDragActive.current)
                                    return;
                                const delta = e.clientX - inlineDragLastX.current;
                                inlineDragLastX.current = e.clientX;
                                inlineDragAccum.current += delta;
                                const frameCount = inlineRotatorImages.length || 1;
                                if (Math.abs(inlineDragAccum.current) >= 8) {
                                    inlineDragDidMove.current = true;
                                    const steps = Math.floor(Math.abs(inlineDragAccum.current) / 8);
                                    const dir = inlineDragAccum.current > 0 ? 1 : -1;
                                    const newIdx = ((inlineFrameIndex + dir * steps) % frameCount + frameCount) % frameCount;
                                    setInlineFrameIndex(newIdx);
                                    inlineDragAccum.current = inlineDragAccum.current % 8;
                                }
                            }, onClick: () => {
                                if (inlineDragDidMove.current) {
                                    inlineDragDidMove.current = false;
                                    return;
                                }
                                openModal(inlineRotatorImages[0] || OGMainImage, 'rotator', undefined, inlineRotatorImages);
                            }, children: [_jsx(ImageRotator, { images: inlineRotatorImages, height: "100%", controlledIndex: inlineFrameIndex, onIndexChange: setInlineFrameIndex, disableDrag: true, fallbackSrc: OGMainImage, onAllFramesFailed: () => setInlineRotatorFailed(true) }), _jsx("div", { ref: inlinePillWrapperRef, onClick: (e) => e.stopPropagation(), style: {
                                        position: 'absolute',
                                        top: '50%',
                                        left: '50%',
                                        transform: 'translate(-50%, -50%)',
                                        zIndex: 10,
                                        pointerEvents: 'none',
                                    }, children: _jsx(NavPill360, { frameIndex: inlineFrameIndex, frameCount: inlineRotatorImages.length, onFrameChange: setInlineFrameIndex, pillWidth: inlinePillWidth, isVisible: isInlineRotatorHovered || isInlineRotatorDragging, extraClassName: "lightbox-gallery__360-nav--cursor lightbox-gallery__360-nav--cursor-inline", followCursor: true }) })] })) : (_jsx("img", { src: inlineImage, alt: "Imagen Auto", onClick: () => openModal(inlineImage, inlineMediaType) })) }), _jsx("div", { className: "lightbox-gallery__lightbox-thumbnails", children: mediaList.map((media, index) => {
                            const isM3u8 = typeof media === "string" ? media.includes(".m3u8") : (media.url || "").includes(".m3u8");
                            const isVideoObject = typeof media !== "string" && media.type === "video";
                            const hideVideoForLocale = (isM3u8 || isVideoObject) && !i18next.language.includes("es");
                            if (hideVideoForLocale)
                                return null;
                            const isRotator = isRotatorMedia(media);
                            const mediaRotatorFrames = isRotator ? getRotatorFrames(media) : [];
                            const isActive = isRotator
                                ? inlineType === "rotator"
                                : (typeof media === "string" && inlineType === "image" && inlineImage === media);
                            return (_jsx("div", { className: isActive ? 'thumbnails-container thumbnails-container--active' : 'thumbnails-container', children: typeof media === "string" ? (_jsx(_Fragment, { children: media.includes('.m3u8') ? (_jsxs("div", { className: "thumbnail-video", onClick: () => openModal(media, 'video'), children: [_jsx(IconPlayVideo, {}), _jsx("img", { src: getPoster(media), alt: `Thumbnail ${index}`, className: "lightbox-gallery__thumbnail", loading: "lazy" })] })) : (_jsx("img", { src: media, alt: `Thumbnail ${index}`, onClick: () => handleInlineThumbnailClick(media), className: "lightbox-gallery__thumbnail", loading: "lazy" })) })) : (_jsx(_Fragment, { children: media.type === "video" ? (i18next.language.includes("es") && (_jsxs("div", { className: "thumbnail-video", onClick: () => openModal(media.url, media.type, media.videoURL), children: [_jsx(IconPlayVideo, {}), _jsx("img", { src: media.url, alt: "Video thumbnail", className: "image-background-slider", loading: "lazy" })] }))) : isRotator && !inlineRotatorFailed ? (_jsxs("div", { className: "thumbnail-rotator", onClick: () => handleInlineThumbnailClick(media, "rotator", mediaRotatorFrames), children: [_jsx("img", { src: inlineType === "rotator" ? (inlineRotatorImages[inlineFrameIndex] || media.url || mediaRotatorFrames[0]) : (media.url || mediaRotatorFrames[0]), alt: "Car 360\u00B0 preview", className: "image-background-slider", loading: "eager", onError: (e) => { e.currentTarget.src = OGMainImage; } }), _jsx("span", { className: "thumbnail-rotator__badge", children: _jsx(Icon360View, {}) })] })) : (_jsx("img", { src: media.url, onClick: () => handleInlineThumbnailClick(media), alt: "Car image preview", className: "image-background-slider", loading: "lazy" })) })) }, (typeof media === 'string' ? media : media.url || '') + index));
                        }) })] })), _jsx(ModalComponent, { isShown: isModalOpen, hide: toggleModal, customClass: "modal__content--gallery-desktop", modalContent: _jsxs("div", { className: "lightbox-gallery", children: [loading ? (_jsx("div", { className: "spinner", children: "Loading..." })) : type === "video" && i18next.language.includes("es") ? (_jsx(_Fragment, { children: videoURL.includes(".m3u8") ?
                                _jsx(ImagesPreviewVideo, { id: parseInt(crypto.randomUUID()), src: videoURL, poster: getPoster(videoURL), controls: true, classExtra: "lightbox-gallery__video" })
                                :
                                    _jsx("video", { controls: true, preload: "metadata", playsInline: true, poster: videoURL.includes(".m3u8") ? getPoster(videoURL) : currentImageIndex, className: 'lightbox-gallery__video', children: _jsx("source", { src: videoURL, type: "video/mp4" }) }) })) : ((type == "360" || type == "rotator") && !isMobile && !modalRotatorFailed ? (_jsxs("section", { ref: (el) => { modalRotatorSectionRef.current = el; }, className: 'lightbox-gallery__image lightbox-gallery__image--rotator', style: { touchAction: 'none', cursor: modalDragActive.current ? 'grabbing' : 'grab' }, onMouseEnter: () => setIsModalRotatorHovered(true), onMouseLeave: () => setIsModalRotatorHovered(false), onPointerDown: (e) => {
                                e.preventDefault();
                                modalDragActive.current = true;
                                modalDragLastX.current = e.clientX;
                                modalDragAccum.current = 0;
                                setIsModalRotatorDragging(true);
                                e.currentTarget.setPointerCapture(e.pointerId);
                            }, onPointerUp: () => {
                                modalDragActive.current = false;
                                setIsModalRotatorDragging(false);
                            }, onPointerLeave: () => {
                                modalDragActive.current = false;
                                setIsModalRotatorDragging(false);
                            }, onPointerMove: (e) => {
                                var _a;
                                // Update pill position (direct DOM, no re-render)
                                const rect = (_a = modalRotatorSectionRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
                                if (rect && modalPillWrapperRef.current) {
                                    const pctX = Math.max(5, Math.min(95, ((e.clientX - rect.left) / rect.width) * 100));
                                    const pctY = Math.max(5, Math.min(95, ((e.clientY - rect.top) / rect.height) * 100));
                                    modalPillWrapperRef.current.style.top = `${pctY}%`;
                                    modalPillWrapperRef.current.style.left = `${pctX}%`;
                                }
                                // Handle drag rotation
                                if (!modalDragActive.current)
                                    return;
                                const delta = e.clientX - modalDragLastX.current;
                                modalDragLastX.current = e.clientX;
                                modalDragAccum.current += delta;
                                const frameCount = rotatorImages.length || 1;
                                if (Math.abs(modalDragAccum.current) >= 8) {
                                    const steps = Math.floor(Math.abs(modalDragAccum.current) / 8);
                                    const dir = modalDragAccum.current > 0 ? 1 : -1;
                                    const newIdx = ((rotatorFrameIndex + dir * steps) % frameCount + frameCount) % frameCount;
                                    setRotatorFrameIndex(newIdx);
                                    modalDragAccum.current = modalDragAccum.current % 8;
                                }
                            }, children: [_jsx(ImageRotator, { images: rotatorImages.length ? rotatorImages : [currentImageIndex], height: "100%", controlledIndex: rotatorFrameIndex, onIndexChange: setRotatorFrameIndex, disableDrag: true, fallbackSrc: OGMainImage, onAllFramesFailed: () => setModalRotatorFailed(true) }), _jsx("div", { ref: modalPillWrapperRef, style: { position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 10, pointerEvents: 'none' }, children: _jsx(NavPill360, { frameIndex: rotatorFrameIndex, frameCount: rotatorImages.length || 1, onFrameChange: setRotatorFrameIndex, dragStepPx: modalPillDragStep, pillWidth: 106, pillHeight: 29, isVisible: isModalRotatorHovered || isModalRotatorDragging, extraClassName: "lightbox-gallery__360-nav--cursor lightbox-gallery__360-nav--cursor-modal", followCursor: true }) })] })) : (_jsx("img", { src: currentImageIndex, alt: `Car image preview ${currentImageIndex}`, className: 'lightbox-gallery__image' }))), _jsx("div", { className: "lightbox-gallery__thumbnails", children: mediaList.map((media, index) => {
                                const isM3u8 = typeof media === "string" ? media.includes(".m3u8") : (media.url || "").includes(".m3u8");
                                const isVideoObject = typeof media !== "string" && media.type === "video";
                                const hideVideoForLocale = (isM3u8 || isVideoObject) && !i18next.language.includes("es");
                                if (hideVideoForLocale)
                                    return null;
                                const isRotator = isRotatorMedia(media);
                                const mediaRotatorFrames = isRotator && typeof media !== "string" ? getRotatorFrames(media) : [];
                                const isActive = (typeof media !== "string" && type === "video" && currentImageIndex === media.url && media.type === "video" && i18next.language.includes("es")) ||
                                    (typeof media === "string" && type === "image" && currentImageIndex === media) ||
                                    (typeof media === "string" && (media === null || media === void 0 ? void 0 : media.includes('.m3u8')) && i18next.language.includes("es") && currentImageIndex === media) ||
                                    (isRotator && type === "rotator");
                                return (_jsx("span", { className: `thumbnails-container ${isActive ? 'thumbnails-container--active' : ''}`, children: typeof media === "string" ? (_jsx(_Fragment, { children: // if the string contains the word '.m3u8' then set it as a video
                                        media.includes('.m3u8') && i18next.language.includes("es") ? (_jsxs("div", { className: "thumbnail-video", onClick: () => handleMediaSelection(media, 'video', media), children: [_jsx(IconPlayVideo, {}), _jsx("img", { src: getPoster(media), alt: `Thumbnail ${index}`, className: "lightbox-gallery__thumbnail", loading: "lazy" })] })) : (_jsx("img", { src: media, alt: `Thumbnail image ${index}`, onClick: () => handleMediaSelection(media), className: "lightbox-gallery__thumbnail", loading: "lazy" })) })) : (_jsx(_Fragment, { children: media.type === "video" ? (i18next.language.includes("es") &&
                                            _jsxs("div", { className: "thumbnail-video", onClick: () => handleMediaSelection(media.url, media.type, media.videoURL), children: [_jsx(IconPlayVideo, {}), _jsx("img", { src: media.url, alt: "Video thumbnail", className: "image-background-slider", loading: "lazy" })] })) :
                                            isRotator && !modalRotatorFailed ? (_jsxs("div", { className: "thumbnail-rotator", onClick: () => handleMediaSelection(media.url || mediaRotatorFrames[0] || "", "rotator", undefined, mediaRotatorFrames), children: [_jsx("img", { src: mediaRotatorFrames[rotatorFrameIndex] || media.url || mediaRotatorFrames[0], alt: "Car 360\u00B0", className: "image-background-slider", loading: "eager", onError: (e) => { e.currentTarget.src = OGMainImage; } }), _jsx("span", { className: "thumbnail-rotator__badge", children: _jsx(Icon360View, {}) })] })) :
                                                (_jsx("img", { src: media.url, onClick: () => handleMediaSelection(media.url, media.type), alt: "Car image", className: "image-background-slider", loading: "lazy" })) })) }, index));
                            }) })] }) })] }));
};
export default LightboxGalleryImage;
